import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowRight, Plus, Users, Copy, Share2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { pickColor } from "@/lib/palette";

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dayLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "OGGI";
  if (diff === 1) return "DOMANI";
  return `${WEEKDAYS[d.getDay()].toUpperCase()} ${d.getDate()} ${MONTHS[d.getMonth()].toUpperCase()}`;
}

export default function GroupsList() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/groups");
      setGroups(data.groups || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeId = user?.active_group_id || user?.group_id;

  const switchGroup = async (id) => {
    try {
      await api.post(`/groups/switch/${id}`);
      await refresh();
      toast.success("Gruppo attivo aggiornato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyCode = async (g) => {
    try {
      await navigator.clipboard.writeText(g.invite_code);
      setCopiedId(g.id);
      toast.success("Codice copiato");
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const shareGroup = async (g) => {
    const url = `${window.location.origin}/?invite=${encodeURIComponent(g.invite_code)}`;
    const text = `Unisciti al gruppo "${g.name}" su Hopzigo`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Hopzigo", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiato");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non disponibile");
    }
  };

  return (
    <div className="anim-slide-up max-w-2xl mx-auto" data-testid="groups-list-page">
      <Link to="/home" className="text-sm font-bold text-[#1A2A6C] inline-flex items-center gap-1 hover:underline mb-3" data-testid="groups-list-back">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </Link>

      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#F5F6FA] text-[#1A2A6C]">
          <Users className="w-5 h-5" />
        </div>
        <h1 className="font-heading text-3xl font-black text-[#1A1D29] leading-tight">{t("groups.title")}</h1>
      </div>
      <p className="text-sm text-[#8A91A8] mb-4">{t("groups.subtitle")}</p>

      <div className="mb-5">
        <Link
          to="/onboarding"
          className="brut-btn inline-flex w-full sm:w-auto justify-center"
          data-testid="groups-list-create-button"
        >
          <Plus className="w-4 h-4" /> {t("groups.create")}
        </Link>
      </div>

      {loading ? (
        <p className="text-center text-[#8A91A8] py-8">Caricamento…</p>
      ) : groups.length === 0 ? (
        <div className="brut-card p-8 text-center">
          <p className="text-[#8A91A8] mb-4">{t("groups.none")}</p>
          <Link to="/onboarding" className="brut-btn inline-flex">
            <Plus className="w-4 h-4" /> {t("groups.create")}
          </Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="groups-list-items">
          {groups.map((g) => {
            const c = pickColor(g.id);
            const isActive = g.id === activeId;
            return (
              <li key={g.id} className={`brut-card p-4 sm:p-5 ${isActive ? "ring-2 ring-[#26B5A8]/40" : ""}`} data-testid={`group-row-${g.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 grid place-items-center rounded-2xl font-heading font-black text-xl shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
                    {g.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading font-bold text-lg text-[#1A1D29] leading-tight truncate">{g.name}</p>
                      {isActive && <span className="brut-chip bg-[#D4F1ED] text-[#26B5A8]">{t("groups.active")}</span>}
                    </div>
                    <p className="text-xs text-[#8A91A8] mt-0.5">
                      {t("groups.code")} · <span className="font-mono font-bold text-[#1A2A6C]">{g.invite_code}</span>
                    </p>
                  </div>
                  <span className={`brut-chip shrink-0 ${g.is_owner ? "bg-[#EFE8FD] text-[#7E3AED]" : "bg-[#F5F6FA] text-[#1A2A6C]"}`}>
                    {g.is_owner ? t("groups.admin") : t("groups.member")}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#ECEEF5] text-center">
                  <div>
                    <p className="font-heading text-2xl font-black text-[#1A2A6C]">{g.members_count}</p>
                    <p className="text-[11px] uppercase tracking-widest font-semibold text-[#8A91A8] mt-0.5">{t("groups.families")}</p>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-black text-[#FF9E1B]">{g.kids_count}</p>
                    <p className="text-[11px] uppercase tracking-widest font-semibold text-[#8A91A8] mt-0.5">{t("groups.kids")}</p>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-black text-[#FF4D5E]">{g.rides_count}</p>
                    <p className="text-[11px] uppercase tracking-widest font-semibold text-[#8A91A8] mt-0.5">{t("groups.rides")}</p>
                  </div>
                </div>

                {g.next_ride ? (
                  <p className="text-sm text-[#5A6178] mt-4">
                    {t("groups.nextRide")} <span className="font-bold text-[#1A1D29]">{dayLabel(g.next_ride.date)} {g.next_ride.time}</span>
                    <span className="block text-xs text-[#8A91A8] truncate">→ {g.next_ride.destination}</span>
                  </p>
                ) : (
                  <p className="text-sm text-[#8A91A8] mt-4">{t("groups.noNextRide")}</p>
                )}

                <div className="grid grid-cols-3 gap-2 mt-4">
                  <button
                    onClick={() => copyCode(g)}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-[#F5F6FA] text-[#1A2A6C] text-xs font-bold hover:bg-[#E5E9F5]"
                    data-testid={`group-copy-${g.id}`}
                  >
                    {copiedId === g.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedId === g.id ? t("groups.copied") : t("groups.code")}
                  </button>
                  <button
                    onClick={() => shareGroup(g)}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-[#F5F6FA] text-[#1A2A6C] text-xs font-bold hover:bg-[#E5E9F5]"
                    data-testid={`group-share-${g.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5" /> {t("groups.share")}
                  </button>
                  <button
                    onClick={async () => {
                      if (!isActive) await switchGroup(g.id);
                      nav("/dashboard");
                    }}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-[#1A2A6C] text-white text-xs font-bold hover:bg-[#14205A]"
                    data-testid={`group-open-${g.id}`}
                  >
                    {g.is_owner ? t("groups.manage") : t("groups.open")} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
